import { useEffect, useState } from 'react';

import { Button, Select } from '@mui/material';
import * as React from "react";
import WorldMap from "react-svg-worldmap";
import * as  dash_api from '../../api/Dashboard.tsx';
import { MenuItem } from '@mui/material';
import { InputLabel } from '@mui/material';
import { FormControl } from '@mui/material';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import { Checkbox, ListItemText } from '@mui/material';
import Legend, { LegendItem } from '../Legend.tsx';
import type { CountryContext, Data, DataItem } from "react-svg-worldmap";


var frequencyColor: { [key: string]: string; } = {
    "Undetectable": "#1171f0",
    "Isolated cases": "#eb4034",
    "Low": "#348c4b",
    "Moderate": "#76b5c5",
    "High": "#ba5961",
    "Very High": "#010117",
    "Variable": "#a434eb",
    "Unknown": "#4e9091",
};


function getColor(value: string | undefined) {
    if (value != undefined) {
        if (frequencyColor[value]) {
            var hex_col = frequencyColor[value];
            var r = parseInt(hex_col.slice(1, 3), 16);
            var g = parseInt(hex_col.slice(3, 5), 16);
            var b = parseInt(hex_col.slice(5, 7), 16);
            return "rgb(" + r + "," + g + "," + b + ")";
        }
    }

    return "rgb(200,200,200)";
}


const getStyle
    = ({
        countryValue,
        countryCode,
        minValue,
        maxValue,
        color,
    }: CountryContext<string>) => (
        {
            fill: getColor(countryValue),
        });


export function DashboardPageMap() {
    const [allData, setAllData] = useState<dash_api.GetResisFreqDataResponse | null>(null);         // all data from the server, not filtered
    const [isLoding, setIsLoading] = useState(true);
    const [moaCode, setMoaCode] = useState(null);
    const [pestSciName, setPestSciName] = useState<string | null>(null);
    const [fracMoaCodes, setFracMoaCodes] = useState<string[]>([]);
    const [pestSciNames, setPestSciNames] = useState<string[]>([]);
    const [countries, setCountries] = useState<string[]>([]);

    // when the user selects moa code and pest scientific name, the data is fetched from the server
    const [countAndFreqData, setCountAndFreqData] = useState<dash_api.GetResisFreqDataResponse | null>(null);

    const [frequencies, setFrequencies] = useState<string[]>([]);
    const [selectedFrequencies, setSelectedFrequencies] = useState<string[]>([]);
    const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
    const [legendData, setLegendData] = useState<LegendItem[]>([]);
    const [years, setYears] = useState<number[] | null>(null);
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [filteredMapData, setFilteredMapData] = useState<DataItem<string>[]>([]);

    async function userSelectPestSciName(pestSciName: string | null) {
        setPestSciName(pestSciName);

        const all_data = await dash_api.getResisFreqData(moaCode, pestSciName);
        const frac_moa_codes = all_data.frac_moa;

        setFracMoaCodes(frac_moa_codes);
        setCountAndFreqData(all_data);
    }

    async function userSelectMoaCode(moaCode) {
        setMoaCode(moaCode);

        const all_data = await dash_api.getResisFreqData(moaCode, pestSciName)
        const sci_names = all_data.pest_sci_names;

        setPestSciNames(sci_names);
        setCountAndFreqData(all_data);
    }

    useEffect(() => {
        setCountriesAndFrequenciesFromData(countAndFreqData);
    }, [countAndFreqData]);

    function setCountriesAndFrequenciesFromData(data) {

        if (moaCode && pestSciName) {
            const countries: string[] = [];

            Object.keys(data.countries).forEach(key => {
                countries.push(key);
            });


            setCountries(countries);
            setFrequencies(data.frequencies);
            setSelectedFrequencies(data.frequencies);
            setSelectedCountries(countries);
        }
    }

    /// Gets the starting data
    async function fetchBasicDashData() {
        setIsLoading(true);

        try {
            const result = await dash_api.getDashboardData();

            const moaCodes = result.moaCodes;
            setFracMoaCodes(moaCodes);

            var pestNames: string[] = [];
            for (var i = 0; i < result.pestRecords.length; i++) {
                pestNames.push(result.pestRecords[i].scientific_name);
            }
            setPestSciNames(pestNames);

        } finally {
            setIsLoading(false);
        }
    }

    // Fetches all the data from the server, when the user clicks the button
    async function searchData() {
        setIsLoading(true);
        try {
            const result = await dash_api.getResisFreqData(moaCode, pestSciName);
            const data = result.data;
            const years_data = result.years;
            const sorted_years = years_data.sort((a, b) => a - b);
            setYears(sorted_years);
            if (sorted_years.length > 0) {
                setSelectedYear(sorted_years[0]);
            }


            setAllData(result);
        } finally {
            setIsLoading(false);
        }
    }

    // When a user changes a year, frequency, or country, etc the data is filtered and the map is updated
    useEffect(() => {
        if (allData == null) {
            return;
        }

        const filterAllData = () => {
            let mapData: DataItem<string>[] = [];
            for (var i = 0; i < allData.data.length; i++) {
                const dataItem = allData.data[i];
                if (selectedYear && dataItem.year == selectedYear) {
                    if (selectedFrequencies.indexOf(dataItem.resistance_frequence) == -1) {
                        continue;
                    }

                    if (selectedCountries.indexOf(dataItem.country) == -1) {
                        continue;
                    }

                    const item: DataItem<string> = {
                        country: dataItem.country_code,
                        value: dataItem.resistance_frequence
                    }

                    mapData.push(item);
                }
            }

            return mapData;
        }

        const filteredData = filterAllData();
        setFilteredMapData(filteredData);
        convertDataToLegendItems(filteredData);
    }
        , [selectedYear, allData, selectedFrequencies, selectedCountries]);

    const convertDataToLegendItems = (mapData: DataItem<string>[]) => {
        const legendData: LegendItem[] = [];
        for (var i = 0; i < mapData.length; i++) {
            const value = mapData[i].value;
            if (value != undefined) {
                if (frequencyColor[value]) {
                    const alreadyExists = legendData.find((item) => item.label == value);
                    if (alreadyExists) {
                        continue;
                    }

                    legendData.push({ color: frequencyColor[value], label: value });
                }
            }
        }

        setLegendData(legendData);
    }


    useEffect(() => {
        fetchBasicDashData();
    }, []);


    const handleSetFrequencies = (value) => {
        setSelectedFrequencies(
            typeof value === 'string' ? value.split(',') : value,
        );
    };

    const handleSetCountries = (value) => {
        setSelectedCountries(
            typeof value === 'string' ? value.split(',') : value,
        );
    };

    const handleSetYears = (value: number | string | null) => {
        if (value === null) {
            setSelectedYear(null);
            return;
        }

        if (typeof value === 'string') {
            value = parseInt(value);
        }

        setSelectedYear(value);
    }

    const canSearch = moaCode !== null && pestSciName !== null;


    return (
        <div >
            <div style={{ display: 'flex' }}>
                <div>
                    <FormControl >
                        <InputLabel id="frac-moa-code-label">FRAC MoA Code</InputLabel>
                        <Select
                            labelId="frac-moa-code-label"
                            id="moa-code"
                            value={moaCode}
                            label="FRAC MoA Code"
                            onChange={(e) => userSelectMoaCode(e.target.value)}
                            style={{ width: '300px' }}
                        >
                            {
                                fracMoaCodes && fracMoaCodes.map((c) => {
                                    return <MenuItem value={c}>{c}</MenuItem>
                                })}
                        </Select>
                    </FormControl>

                    <br /><br />

                    <FormControl >
                        <InputLabel id="pest-sci-name-label">Pest scientific name</InputLabel>
                        <Select
                            labelId="pest-sci-name-label"
                            id="pest-sci-name"
                            value={pestSciName}
                            label="Pest scientific name"
                            onChange={(e) => userSelectPestSciName(e.target.value)}
                            style={{ width: '300px' }}
                        >
                            {
                                pestSciNames && pestSciNames.sort((a, b) => a.localeCompare(b)).
                                    map((pest) => {
                                        return <MenuItem value={pest}>{pest}</MenuItem>
                                    })
                            }

                        </Select>

                    </FormControl>

                    {pestSciName && moaCode &&
                        <>
                            <br /><br />
                            <FormControl >

                                <InputLabel id="Country-label">Country</InputLabel>
                                <Select
                                    labelId="Country-label"
                                    id="Country"
                                    value={selectedCountries}
                                    label="Country"
                                    multiple
                                    renderValue={(selected) => selected?.join(', ')}
                                    onChange={(e) => handleSetCountries(e.target.value)}
                                    style={{ width: '300px' }}
                                >
                                    {
                                        countries && countries.sort((a, b) => a.localeCompare(b)).
                                            map((c) => {
                                                return <MenuItem key={c} value={c}>
                                                    <Checkbox checked={(selectedCountries ?? []).indexOf(c) > -1} />
                                                    <ListItemText primary={c} />
                                                </MenuItem>
                                            })}

                                </Select>
                            </FormControl>

                            {/* frequences */}
                            <br /><br />
                            <FormControl >

                                <InputLabel id="Frequencies-label">Frequencies</InputLabel>

                                <Select
                                    labelId="Frequencies-label"
                                    id="Frequencies"
                                    value={selectedFrequencies}
                                    label="Frequencies"
                                    multiple
                                    renderValue={(selected) => selected.join(', ')}
                                    onChange={(e) => handleSetFrequencies(e.target.value)}
                                    style={{ width: '300px' }}
                                >
                                    {
                                        frequencies && frequencies.sort((a, b) => a.localeCompare(b)).
                                            map((c) => {
                                                console.log(c + " " + selectedFrequencies.indexOf(c));

                                                return <MenuItem key={c} value={c}>
                                                    <Checkbox checked={selectedFrequencies.indexOf(c) > -1} />
                                                    <ListItemText primary={c} />
                                                </MenuItem>
                                            })
                                    }

                                </Select>

                            </FormControl>

                            <br /><br />
                            {/* years */}
                            {years &&
                                <FormControl >

                                    <InputLabel id="year-label">Year</InputLabel>
                                    <Select
                                        labelId="year-label"
                                        id="year"
                                        value={selectedYear}
                                        label="Year"
                                        onChange={(e) => handleSetYears(e.target.value)}
                                        style={{ width: '300px' }}
                                    >
                                        {
                                            years && years.map((c) => {
                                                return <MenuItem value={c}>{c}</MenuItem>
                                            })}

                                    </Select>
                                </FormControl>
                            }
                        </>
                    }

                    <br /><br />
                    <Button disabled={!canSearch}
                        variant="contained" color="primary" onClick={searchData}>Search</Button>

                    {/* icon button rotate left */}
                    <br /><br />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setMoaCode(null);
                            setPestSciName(null);
                            setSelectedCountries([]);
                            setCountAndFreqData(null);
                            setYears(null);
                            fetchBasicDashData();
                        }}
                        startIcon={<RotateLeftIcon />}
                    >
                        Reset
                    </Button>
                </div>
                <WorldMap
                    color="white"
                    value-suffix="people"
                    size="xl"
                    data={filteredMapData}
                    richInteraction
                    styleFunction={getStyle}
                />

                <Legend items={legendData} />

            </div>
        </div >

    );
}
